import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { UtensilsCrossed, CupSoda, Cookie, Gift, Star, Loader2, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STAMPS_MAX = 10;

const CARTE_STYLE = {
  plat: {
    label: 'Plat', icon: UtensilsCrossed, reward: '1 plat offert',
    gradient: 'from-amber-500 to-orange-500',
    stampFilled: 'bg-white text-amber-600 border-white shadow-md',
    stampEmpty: 'bg-white/20 border-white/40 text-white/50',
    cardBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
  },
  bubble_tea: {
    label: 'Bubble Tea', icon: CupSoda, reward: '1 bubble tea offert',
    gradient: 'from-purple-500 to-violet-600',
    stampFilled: 'bg-white text-purple-600 border-white shadow-md',
    stampEmpty: 'bg-white/20 border-white/40 text-white/50',
    cardBg: 'bg-gradient-to-br from-purple-500 to-violet-600',
  },
  mochi: {
    label: 'Mochi', icon: Cookie, reward: '1 mochi offert',
    gradient: 'from-pink-500 to-rose-500',
    stampFilled: 'bg-white text-pink-600 border-white shadow-md',
    stampEmpty: 'bg-white/20 border-white/40 text-white/50',
    cardBg: 'bg-gradient-to-br from-pink-500 to-rose-600',
  },
};

export default function MaFidelitePage() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/fidelite/public/${clientId}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError('Carte non trouvee'); setLoading(false); });
  }, [clientId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-white/60" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-6">
        <div className="text-center text-white">
          <Star className="w-16 h-16 mx-auto mb-4 text-amber-400" />
          <h1 className="text-2xl font-bold mb-2">Carte introuvable</h1>
          <p className="text-white/60">Ce lien ne correspond a aucun client</p>
        </div>
      </div>
    );
  }

  const totalRewards = Object.values(data.rewards || {}).reduce((s, r) => s + r.disponibles, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="public-fidelite">
      {/* Header */}
      <div className="px-4 pt-8 pb-4 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 rounded-full mb-4">
          <Star className="w-4 h-4 text-amber-400" />
          <span className="text-amber-300 text-sm font-medium">Chana Poke Thai</span>
        </div>
        <h1 className="text-white text-2xl font-bold" data-testid="client-name">
          {data.prenom} {data.nom}
        </h1>
        <p className="text-white/50 text-sm mt-1">{data.total_visites} visite{data.total_visites > 1 ? 's' : ''}</p>

        {totalRewards > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full animate-pulse">
            <Gift className="w-4 h-4 text-green-400" />
            <span className="text-green-300 font-semibold text-sm">
              {totalRewards} recompense{totalRewards > 1 ? 's' : ''} a utiliser !
            </span>
          </div>
        )}
      </div>

      {/* Cards */}
      <div className="px-4 pb-6 space-y-4 max-w-md mx-auto">
        {['plat', 'bubble_tea', 'mochi'].map(carteId => {
          const style = CARTE_STYLE[carteId];
          const Icon = style.icon;
          const stamps = data[`stamps_${carteId}`] || 0;
          const rewardInfo = data.rewards?.[carteId] || {};
          const hasReward = rewardInfo.disponibles > 0;

          return (
            <div key={carteId} className={`${style.cardBg} rounded-2xl p-5 shadow-xl relative overflow-hidden`}
              data-testid={`public-carte-${carteId}`}>
              {/* Decorative circle */}
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/5 rounded-full" />
              <div className="absolute -right-4 -bottom-12 w-24 h-24 bg-white/5 rounded-full" />

              {/* Header */}
              <div className="flex items-center justify-between mb-4 relative z-10">
                <div className="flex items-center gap-2">
                  <Icon className="w-6 h-6 text-white" />
                  <span className="text-white font-bold text-lg">{style.label}</span>
                </div>
                <span className="text-white/80 text-sm font-medium">{stamps}/{STAMPS_MAX}</span>
              </div>

              {/* Stamps grid */}
              <div className="flex justify-center gap-2 mb-4 relative z-10">
                {Array.from({ length: STAMPS_MAX }, (_, i) => (
                  <div key={i} className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${i < stamps ? style.stampFilled : style.stampEmpty}`}>
                    {i < stamps ? <Star className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden mb-3 relative z-10">
                <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${(stamps / STAMPS_MAX) * 100}%` }} />
              </div>

              {/* Reward info */}
              <div className="relative z-10">
                {hasReward ? (
                  <div className="flex items-center justify-center gap-2 py-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Gift className="w-4 h-4 text-white" />
                    <span className="text-white font-semibold text-sm">
                      {rewardInfo.disponibles} {style.reward} a recuperer !
                    </span>
                  </div>
                ) : stamps >= STAMPS_MAX - 1 ? (
                  <p className="text-center text-white/80 text-sm font-medium">
                    Plus qu'1 tampon pour {style.reward} !
                  </p>
                ) : (
                  <p className="text-center text-white/60 text-xs">
                    Encore {STAMPS_MAX - stamps} tampon{STAMPS_MAX - stamps > 1 ? 's' : ''} pour {style.reward}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent visits */}
      {data.visites?.length > 0 && (
        <div className="px-4 pb-8 max-w-md mx-auto">
          <h3 className="text-white/60 text-sm font-medium mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Dernieres visites
          </h3>
          <div className="space-y-2">
            {data.visites.map(v => {
              const carteStyle = CARTE_STYLE[v.carte] || {};
              const CarteIcon = carteStyle.icon || Star;
              const isReward = v.type === 'reward_gagne' || v.type === 'reward_utilise';
              return (
                <div key={v.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${isReward ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/5 border border-white/10'}`}>
                  <CarteIcon className={`w-4 h-4 flex-shrink-0 ${isReward ? 'text-green-400' : 'text-white/40'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${isReward ? 'text-green-300 font-medium' : 'text-white/70'}`}>
                      {v.type === 'visite' ? `Tampon ${carteStyle.label || ''}` : v.type === 'reward_gagne' ? 'Recompense gagnee !' : 'Recompense utilisee'}
                    </p>
                  </div>
                  <span className="text-white/40 text-xs flex-shrink-0">{new Date(v.date).toLocaleDateString('fr-FR')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center pb-8 px-4">
        <p className="text-white/30 text-xs">Chana Poke Thai — Programme Fidelite</p>
      </div>
    </div>
  );
}
